import { BellIcon, WarningCircleIcon } from '@phosphor-icons/react';
import classNames from 'classnames';
import { useScrollFade } from 'hooks/useScrollFade';
import type { FC } from 'react';
import { Button } from 'ui-library/components/Button/Button';
import { EmptyState } from 'ui-library/components/EmptyState/EmptyState';
import type { NotificationsResponse } from '../../api/notifications/fetchNotifications';
import s from './SharedWithMeDropdown.module.scss';
import { SharedWithMeItem } from './SharedWithMeItem';
import { SharedWithMeItemSkeleton } from './SharedWithMeItemSkeleton';

const SKELETON_COUNT = 4;

type SharedWithMePanelT = {
  isLoading: boolean;
  isError: boolean;
  notifications?: NotificationsResponse;
  onRetry: () => void;
  setIsOpen: (isOpen: boolean) => void;
};

export const SharedWithMePanel: FC<SharedWithMePanelT> = ({
  isLoading,
  isError,
  notifications,
  onRetry,
  setIsOpen,
}) => {
  'use memo';

  const { ref: listRef, fade } = useScrollFade<HTMLDivElement>();

  const renderContent = () => {
    if (isLoading) {
      return Array.from({ length: SKELETON_COUNT }, (_, index) => <SharedWithMeItemSkeleton key={index} />);
    }

    if (isError && !notifications) {
      return (
        <EmptyState
          variant="transparent"
          icon={<WarningCircleIcon weight="bold" />}
          title="Couldn't load notifications"
          action={
            <Button variant="tertiary" size="sm" onClick={onRetry}>
              Retry
            </Button>
          }
        />
      );
    }

    if (notifications?.length === 0) {
      return <EmptyState variant="transparent" icon={<BellIcon weight="bold" />} title="No notifications" />;
    }

    return notifications?.map(notification => (
      <SharedWithMeItem key={notification.notificationId} notification={notification} setIsOpen={setIsOpen} />
    ));
  };

  return (
    <>
      <div className={s.menuHeader}>
        <h4 className={s.menuTitle}>Notifications</h4>
      </div>
      <div className={s.divider} />
      <div
        ref={listRef}
        className={classNames(s.notificationsList, fade.top && s.fadeTop, fade.bottom && s.fadeBottom)}
      >
        {renderContent()}
      </div>
    </>
  );
};
